import React from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import './HomePage.css';

/**
 * Represents the HomePage component, providing functionality to download an Excel template
 * and navigate to an upload page. Displays instructions and requirements for users
 * interacting with the template process.
 *
 * @return {JSX.Element} The HomePage component, including buttons for downloading
 * the template, uploading the template, and detailed steps and requirements for the process.
 */
function HomePage() {
  const navigate = useNavigate();

  const handleDownloadTemplate = async () => {
    try {
      const response = await apiService.downloadTemplate();

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'thnks_address_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Failed to download template. Please try again.');
    }
  };

  const handleUploadClick = () => {
    navigate('/upload');
  };

  return (
    <div className="home-page">
      <p className="home-page-intro">Choose your Thnks item in the next step after uploading your completed template.</p>
      <div className="action-cards">
        <div className="action-card">
          <div className="card-icon">📥</div>
          <h2>Download Template</h2>
          <p>Get the Excel template with the correct column format for your address data</p>
          <button className="btn-primary" onClick={handleDownloadTemplate}>
            Download Template
          </button>
        </div>

        <div className="action-card">
          <div className="card-icon">📤</div>
          <h2>Upload Template</h2>
          <p>Upload your completed spreadsheet for address validation and processing</p>
          <button className="btn-primary" onClick={handleUploadClick}>
            Upload Template
          </button>
        </div>
      </div>

      <div className="info-section">
        <h3>How It Works</h3>
        <ol className="process-list">
          <li>
            <span className="step-number">1</span>
            Download the template and fill in your address data
          </li>
          <li>
            <span className="step-number">2</span>
            Upload your completed spreadsheet for validation
          </li>
          <li>
            <span className="step-number">3</span>
            Review and correct any invalid columns
          </li>
          <li>
            <span className="step-number">4</span>
            Select your Thnks and personalize your message
          </li>
            <li>
            <span className="step-number">5</span>
            Complete! Once shipped, tracking information will be sent your way via email
          </li>
        </ol>
      </div>

      <div className="requirements-section">
        <h3>File Requirements</h3>
        <ul className="requirements-list">
          <li>Accepted formats: CSV, XLSX, XLSM</li>
          <li>Maximum file size: 10MB</li>
          <li>All required columns must be present</li>
          <li>Country codes will be automatically standardized</li>
            <br/>
            <p>Questions: Please reach out to our team at <a href="mailto:support@thnks.com">support@thnks.com</a>.</p>
        </ul>
      </div>
    </div>
  );
}

export default HomePage;